import React, { useMemo } from "react";
import classnames from "classnames";
import { useGlobal } from "reactn";
import { useLocation, Link } from "react-router-dom";
import { getSummonerProfileContext } from "components/SummonerProfiles/SummonerProfileContext";
import { useTable, useSortBy, usePagination } from "react-table";
import NoTableData from "components/NoTableData";
import { TooltipContainer } from "@outplayed/tooltips";
import { useLightMode } from "hooks/general-hooks";
import { numberWithCommas, getKdaColorClassName, getWinRateColor } from "lib/general-helper.js";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { formatSummonerChampionStats } from "api/data_parser/summoner_champion_stats";
import { ReactComponent as Penta } from "svg/multikills/penta.svg";
import { ReactComponent as Quadra } from "svg/multikills/quadra.svg";
import { ReactComponent as Triple } from "svg/multikills/triple.svg";
import { ReactComponent as Double } from "svg/multikills/double.svg";
import LPGainArrows from "components/SummonerProfiles/views/ProfileOverview/LPBlock/LPGainArrows";

const ChampionStatsTable = (props) => {
  const { getChampionImg, getChampionName, getNormalizedChampionName } = getRiotAssetsContext();
  const location = useLocation();
  const context = getSummonerProfileContext();
  const { summonerName, region } = context;
  const [windowBreakpoint] = useGlobal("responsive");
  const { lightModeOn } = useLightMode();

  const { data, isRanked, hideLP } = props;

  const tableData = useMemo(() => {
    const champions = formatSummonerChampionStats(data || []);
    champions.sort((a, b) => {
      const matchDiff = b.played.totalMatches - a.played.totalMatches;
      if (matchDiff > 0) {
        return 1;
      } else if (matchDiff < 0) {
        return -1;
      } else {
        const champNameA = getChampionName(a.championId);
        const champNameB = getChampionName(b.championId);
        return champNameA.localeCompare(champNameB);
      }
    });

    return champions;
  }, [data]);

  const isMobile = ["MOBILE_LARGE", "MOBILE_MEDIUM", "MOBILE_SMALL"].includes(windowBreakpoint);
  // const isMobile = false;
  const defaultColWidth = 76;
  let columns = useMemo(
    () => [
      {
        Header: "Rank",
        headerClassName: "rank-cell",
        className: "rank-cell",
        accessor: "totalMatches",
        Cell: ({ row }) => <span>{row.index + 1}</span>,
        minWidth: 30,
        maxWidth: 60,
        sortType: (a, b) => {
          const matchDiff = b.values.totalMatches - a.values.totalMatches;
          if (matchDiff > 0) {
            return 1;
          } else if (matchDiff < 0) {
            return -1;
          } else {
            const champNameA = getChampionName(a.values.championId);
            const champNameB = getChampionName(b.values.championId);
            return champNameA.localeCompare(champNameB);
          }
        },
      },
      {
        Header: "Champion",
        headerClassName: "champion-cell",
        className: "champion-cell",
        accessor: "championId",
        minWidth: 130,
        // maxWidth: 180,
        sortType: (a, b) => {
          const champNameA = getChampionName(a.values.championId);
          const champNameB = getChampionName(b.values.championId);
          return champNameA.localeCompare(champNameB);
        },
        Cell: (props) => {
          const championImg = getChampionImg(props.value);
          const championName = getChampionName(props.value);
          const normalizedChampionName = getNormalizedChampionName(props.value);

          return (
            <div className="champion">
              <div className="champion-face">
                <img src={championImg} />
              </div>
              <span className="champion-name">{championName}</span>
            </div>
          );
        },
      },
      // {
      //   Header: "Played",
      //   accessor: "played",
      //   className: "played",
      //   id: "played",
      //   minWidth: 60, //140,
      //   sortType: (a, b) => {
      //     return a.totalMatches - b.totalMatches;
      //   },
      //   Cell: props => {
      //     return <span>{numberWithCommas(props.value.totalMatches || 0)}</span>
      //   }
      // },
      {
        Header: "Win Rate",
        headerClassName: "win-rate-cell",
        className: "win-rate-cell",
        accessor: "winRate",
        minWidth: 120,
        maxWidth: 140,
        sortDescFirst: true,
        Cell: ({ row: { original } }) => {
          const roundedWinRate = Math.round(original.winRate);
          const winRateColor = getWinRateColor(roundedWinRate, lightModeOn, false);
          // const winRateClassNameColor = getWinRateColorClassName(roundedWinRate);
          return (
            <div className="champion-rates">
              <strong style={{ color: winRateColor }}>{`${roundedWinRate}%`}</strong>
              <span className="backslash"> / </span>
              <span className="match-record">
                {original.played.wins}W {original.played.losses}L
              </span>
              {/*<div className="champion-rates_values">
              <div>
                <strong style={{color: winRateColor}}>{`${roundedWinRate}%`}</strong>
              </div>
              <div className="forward-slash">/</div>
              <div className="match-record">{original.played.wins}W {original.played.losses}L</div>
            </div>*/}
              {/*<div className="champion-rates_bar">
              <div className="percentage" style={{width: `${roundedWinRate}%`, backgroundColor: winRateColor}}/>
            </div>*/}
            </div>
          );
        },
      },
      {
        Header: "KDA",
        headerClassName: "kda-cell",
        className: "kda-cell",
        accessor: "kda",
        minWidth: 100,
        maxWidth: 120,
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.values.kda.ratio - b.values.kda.ratio;
        },
        Cell: ({ value }) => (
          <div className="kda">
            <div className={getKdaColorClassName(value.ratio)}>
              <strong>{value.ratio}</strong>
            </div>
            <span>
              <strong>{value.avgKills}</strong>
              <span className="backslash"> / </span>
              <strong>{value.avgDeaths}</strong>
              <span className="backslash"> / </span>
              <strong>{value.avgAssists}</strong>
            </span>
          </div>
        ),
      },
      {
        Header: () => (
          <div>
            <div>LP</div>
            <div>Gain</div>
          </div>
        ),
        headerClassName: classnames("lp-gain-cell", { hidden: !isRanked || hideLP }),
        className: classnames("lp-gain-cell", { hidden: !isRanked || hideLP }),
        accessor: "lpAvg",
        sortDescFirst: true,
        sortType: (a, b) => {
          //console.log(a)
          return a.values.lpAvg - b.values.lpAvg;
        },
        minWidth: 55,
        maxWidth: 70,
        Cell: (props) => <LPGainArrows lpAvg={props.value} />,
      },
      {
        Header: () => (
          <div>
            <div>Max</div>
            <div>Kills</div>
          </div>
        ),
        headerClassName: "max-kills-cell",
        className: "max-kills-cell",
        accessor: "maxKills",
        sortDescFirst: true,
        minWidth: 55,
        maxWidth: 70,
        Cell: (props) => <span>{props.value}</span>,
      },
      {
        Header: () => (
          <div>
            <div>Max</div>
            <div>Deaths</div>
          </div>
        ),
        headerClassName: "max-deaths-cell",
        className: "max-deaths-cell",
        accessor: "maxDeaths",
        sortDescFirst: true,
        minWidth: 55,
        maxWidth: 70,
        Cell: (props) => <span>{props.value}</span>,
      },
      {
        Header: "CS",
        headerClassName: "cs-cell",
        className: "cs-cell",
        accessor: "avgCs",
        sortDescFirst: true,
        minWidth: 70,
        maxWidth: 80,
        Cell: (props) => <span>{props.value}</span>,
      },
      {
        Header: () => <div>Damage</div>,
        headerClassName: "damage-cell",
        className: "damage-cell",
        accessor: "avgDamage",
        sortDescFirst: true,
        minWidth: 70,
        maxWidth: 80,
        Cell: (props) => <span>{numberWithCommas(props.value)}</span>,
      },
      {
        Header: "Gold",
        headerClassName: "gold-cell",
        className: "gold-cell",
        accessor: "avgGold",
        sortDescFirst: true,
        minWidth: 70,
        maxWidth: 80,
        Cell: (props) => <span>{numberWithCommas(props.value)}</span>,
      },
      // {
      //   Header: () => (
      //     <div>
      //       <div>{"Avg. Dmg."}</div>
      //       <div>Taken</div>
      //     </div>
      //   ),
      //   accessor: "avgDamageTaken",
      //   minWidth: defaultColWidth,
      //   Cell: props => <span>{(numberWithCommas(props.value))}</span>
      // },
      {
        Header: () => (
          <TooltipContainer tooltipInfo="Double Kill">
            <div className="multikill-icon-container">
              <Double className="double-kill-svg" />
            </div>
          </TooltipContainer>
        ),
        headerClassName: "multikill-cell",
        className: "multikill-cell",
        accessor: "doubleKills",
        sortDescFirst: true,
        minWidth: 50,
        maxWidth: 50,
        Cell: (props) => {
          return props.value === 0 ? (
            <span style={{ color: lightModeOn ? "#8489B2" : "#4F4F60" }}>&mdash;</span>
          ) : (
            <strong style={{ color: lightModeOn ? "#5F648A" : "#cddcfe" }}>{numberWithCommas(props.value)}</strong>
          );
        },
      },
      {
        Header: () => (
          <TooltipContainer tooltipInfo="Triple Kill">
            <div className="multikill-icon-container">
              <Triple />
            </div>
          </TooltipContainer>
        ),
        headerClassName: "multikill-cell",
        className: "multikill-cell",
        accessor: "tripleKills",
        sortDescFirst: true,
        minWidth: 50,
        maxWidth: 50,
        Cell: (props) => {
          return props.value === 0 ? (
            <span style={{ color: lightModeOn ? "#8489B2" : "#4F4F60" }}>&mdash;</span>
          ) : (
            <strong style={{ color: "#59d2d9" }}>{numberWithCommas(props.value)}</strong>
          );
        },
      },
      {
        Header: () => (
          <TooltipContainer tooltipInfo="Quadra Kill">
            <div className="multikill-icon-container">
              <Quadra />
            </div>
          </TooltipContainer>
        ),
        headerClassName: "multikill-cell",
        className: "multikill-cell",
        accessor: "quadraKills",
        sortDescFirst: true,
        minWidth: 50,
        maxWidth: 50,
        Cell: (props) => {
          return props.value === 0 ? (
            <span style={{ color: lightModeOn ? "#8489B2" : "#4F4F60" }}>&mdash;</span>
          ) : (
            <strong style={{ color: "#00a7ff" }}>{numberWithCommas(props.value)}</strong>
          );
        },
      },
      {
        Header: () => (
          <TooltipContainer tooltipInfo="Penta Kill">
            <div className="multikill-icon-container">
              <Penta />
            </div>
          </TooltipContainer>
        ),
        headerClassName: "multikill-cell",
        className: "multikill-cell",
        accessor: "pentaKills",
        sortDescFirst: true,
        minWidth: 50,
        maxWidth: 50,
        Cell: (props) => {
          return props.value === 0 ? (
            <span style={{ color: lightModeOn ? "#8489B2" : "#4F4F60" }}>&mdash;</span>
          ) : (
            <strong style={{ color: "#FF9B00" }}>{numberWithCommas(props.value)}</strong>
          );
        },
      },
    ],
    [lightModeOn],
  );

  const columnStyle = (column) => ({
    minWidth: column.totalMinWidth,
    maxWidth: column.totalMaxWidth,
    flex: `1 1 100%`,
  });

  const { state, getTableProps, getTableBodyProps, headerGroups, rows, preSortedRows, prepareRow, setHiddenColumns } = useTable(
    {
      columns,
      data: tableData,
      disableSortRemove: true,
    },
    useSortBy,
  );

  return (
    <div {...getTableProps({ className: "content-section ReactTable ugg-table-2 profile-champion-stats-table" })}>
      <div className="rt-thead -header">
        {headerGroups.map((headerGroup, index) => (
          <div key={index} {...headerGroup.getHeaderGroupProps({ className: "rt-tr" })}>
            {headerGroup.headers.map((column, index) => {
              const thClassNames = classnames([
                "rt-th",
                column.headerClassName,
                column.isSorted && !column.isSortedDesc ? "-sort-asc" : "",
                column.isSorted && column.isSortedDesc ? "-sort-desc" : "",
              ]);

              return (
                <div
                  key={index}
                  {...column.getHeaderProps(column.getSortByToggleProps({ className: thClassNames, style: columnStyle(column) }))}
                >
                  {column.render("Header")}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div {...getTableBodyProps({ className: "rt-tbody" })}>
        {rows.length === 0 ? (
          <NoTableData />
        ) : (
          rows.map((row, i) => {
            prepareRow(row);
            return (
              <div key={i} className="rt-tr-group">
                <div {...row.getRowProps({ className: "rt-tr" })}>
                  {row.cells.map((cell, index) => {
                    const tdClassNames = classnames("rt-td", cell.column.className, {
                      sorted: index !== 0 && cell.column.isSorted,
                      "is-in-odd-row": i % 2 === 0,
                    });

                    return (
                      <div key={index} {...cell.getCellProps({ className: tdClassNames, style: columnStyle(cell.column) })}>
                        {cell.render("Cell", { index: i })}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ChampionStatsTable;
